import { useEffect, useRef, useState } from 'react';

import { cn } from '@/marketing/lib/utils';
import { Button } from '@/marketing/components/ui/button';
import type { ContentTrackingRow } from '@/marketing/models/contentTracking';

interface ContentTrackingNotesCellProps {
  row: ContentTrackingRow;
  disabled: boolean;
  notes: string;
  onNotesChange: (notes: string) => void;
  onSaveNotes: (notes: string) => Promise<unknown>;
  onSave: (patch: { notes?: string; cancelled?: boolean; cancelReason?: string; topVideoUrls?: string[] }) => void;
}

// The server rejects cancelled creators with Top 10 picks, so cancellation clears selections in the same metadata write.
export function ContentTrackingNotesCell({ row, disabled, notes, onNotesChange, onSaveNotes, onSave }: ContentTrackingNotesCellProps) {
  const [reason, setReason] = useState(row.metadata.cancelReason);
  const [cancelling, setCancelling] = useState(false);
  const [noteStatus, setNoteStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const noteInFlight = useRef(false);
  const notesChanged = notes.trim() !== row.metadata.notes;
  useEffect(() => setReason(row.metadata.cancelReason), [row.metadata.cancelReason]);

  const commitNotes = async () => {
    if (disabled || noteInFlight.current || !notesChanged) return;
    noteInFlight.current = true;
    setNoteStatus('saving');
    try {
      await onSaveNotes(notes);
      setNoteStatus('saved');
    } catch {
      setNoteStatus('error');
    } finally {
      noteInFlight.current = false;
    }
  };
  const confirmCancel = () => {
    if (disabled || !reason.trim()) {
      return;
    }
    onSave({ cancelled: true, cancelReason: reason.trim(), topVideoUrls: [] });
    setCancelling(false);
  };

  return (
    <div className="tw-flex tw-min-w-[200px] tw-flex-col tw-gap-1">
      <textarea
        value={notes}
        disabled={disabled || noteStatus === 'saving'}
        rows={2}
        placeholder="Notes"
        onChange={(event) => {
          onNotesChange(event.target.value);
          setNoteStatus('idle');
        }}
        aria-label={`Notes for ${row.handle ?? row.influencerId}`}
        className="tw-w-full tw-resize-none tw-rounded tw-border tw-border-border tw-bg-background tw-px-2 tw-py-1 tw-text-[11px]"
      />
      <div className="tw-flex tw-items-center tw-gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="tw-h-7 tw-px-2 tw-text-[11px]"
          aria-label={`Save notes for ${row.handle ?? row.influencerId}`}
          disabled={disabled || noteStatus === 'saving' || !notesChanged}
          onClick={() => void commitNotes()}
        >
          {noteStatus === 'saving' ? 'Saving…' : 'Save'}
        </Button>
        <span role="status" className={cn('tw-text-[11px]', noteStatus === 'error' ? 'tw-text-rose-700' : 'tw-text-muted-foreground')}>
          {noteStatus === 'error' && notesChanged ? 'Could not confirm save. Try again.' : notesChanged ? 'Unsaved changes' : noteStatus === 'saved' ? 'Saved' : ''}
        </span>
      </div>
      {row.metadata.cancelled ? (
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-[11px]">
          <span className="tw-rounded-full tw-bg-rose-100 tw-px-2 tw-py-0.5 tw-font-semibold tw-text-rose-700">
            Cancelled
          </span>
          <span className="tw-truncate tw-text-muted-foreground" title={row.metadata.cancelReason}>
            {row.metadata.cancelReason}
          </span>
          <button
            type="button"
            disabled={disabled}
            className="tw-ml-auto tw-text-blue-700 hover:tw-underline"
            onClick={() => onSave({ cancelled: false, cancelReason: '' })}
          >
            Restore
          </button>
        </div>
      ) : cancelling ? (
        <div className="tw-flex tw-items-center tw-gap-1">
          <input
            autoFocus
            disabled={disabled}
            value={reason}
            placeholder="Cancel reason"
            onChange={(event) => setReason(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                confirmCancel();
              }
              if (event.key === 'Escape') {
                setCancelling(false);
              }
            }}
            aria-label="Cancellation reason"
            className="tw-h-6 tw-flex-1 tw-rounded tw-border tw-border-border tw-px-1 tw-text-[11px]"
          />
          <button
            type="button"
            className={cn('tw-text-[11px] tw-text-rose-700', !reason.trim() && 'tw-opacity-50')}
            disabled={disabled || !reason.trim()}
            onClick={confirmCancel}
          >
            Confirm
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          className="tw-self-start tw-text-[11px] tw-text-muted-foreground hover:tw-text-rose-700"
          onClick={() => setCancelling(true)}
        >
          Cancel creator
        </button>
      )}
    </div>
  );
}
